import { useState } from 'react';
import { Download, Upload, Loader2 } from 'lucide-react';
import { candidateService } from '@core/supabase/services/candidateService';
import { CANDIDATE_CSV_HEADERS, parseCandidatesCsv } from './parseCandidatesCsv';

const TEMPLATE_CSV = `${CANDIDATE_CSV_HEADERS.join(',')}
"Jane Doe",jane@example.com,+919999999999,https://linkedin.com/in/jane,Acme Corp,Senior Engineer,Bengaluru,LinkedIn,5,"Java;Spring;AWS","Referred by VP",referral,hot`;

export function RoleFitBulkImportTab({
  orgId,
  userId,
  onImported,
}: {
  orgId: string;
  userId: string;
  onImported: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidates_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    if (!file) {
      setMessage({ type: 'err', text: 'Choose a .csv file first.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const text = await file.text();
      const { rows, errors } = parseCandidatesCsv(text);
      if (errors.length && rows.length === 0) {
        setMessage({ type: 'err', text: errors.join(' ') });
        setBusy(false);
        return;
      }
      let ok = 0;
      const rowErrors: string[] = [...errors];
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        try {
          await candidateService.create({
            org_id: orgId,
            created_by: userId,
            full_name: r.full_name,
            email: r.email,
            phone: r.phone,
            linkedin_url: r.linkedin_url,
            current_company: r.current_company,
            current_title: r.current_title,
            location: r.location,
            source_channel: r.source_channel,
            years_exp: r.years_exp,
            parsed_skills: r.parsed_skills.length ? r.parsed_skills : null,
            education: null,
            tags: r.tags,
            notes: r.notes,
            avatar_url: null,
            base_resume_url: null,
            recruiter_rating: null,
            is_active: true,
          });
          ok++;
        } catch (e: unknown) {
          rowErrors.push(`Row ${i + 2}: ${e instanceof Error ? e.message : 'failed'}`);
        }
      }
      if (rowErrors.length) {
        setMessage({
          type: ok > 0 ? 'ok' : 'err',
          text: `Imported ${ok} of ${rows.length}. ${rowErrors.slice(0, 5).join(' ')}${rowErrors.length > 5 ? '…' : ''}`,
        });
      } else {
        setMessage({ type: 'ok', text: `Imported ${ok} candidate(s).` });
      }
      setFile(null);
      onImported();
    } catch (e: unknown) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : 'Import failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4 text-sm">
      <p className="text-gray-400">
        Upload a UTF-8 CSV with a header row. Required columns: <code className="text-brand-400">full_name</code>,{' '}
        <code className="text-brand-400">email</code>. Separate multiple skills in{' '}
        <code className="text-gray-500">parsed_skills</code> with semicolons or pipes. Tags can be comma-separated.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={downloadTemplate}>
          <Download className="h-4 w-4" /> Download CSV template
        </button>
      </div>
      <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-3 font-mono text-xs text-gray-500 overflow-x-auto">
        {CANDIDATE_CSV_HEADERS.join(',')}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="file"
          accept=".csv,text/csv"
          className="text-sm text-gray-400 file:mr-3 file:rounded file:border-0 file:bg-gray-800 file:px-3 file:py-1.5 file:text-sm file:text-gray-200"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={runImport} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Import candidates
        </button>
      </div>
      {message && (
        <p className={message.type === 'ok' ? 'text-green-400' : 'text-red-400'}>{message.text}</p>
      )}
    </div>
  );
}
